import { FunnelMapCanvas } from '../../../funnel-map-canvas/FunnelMapCanvas';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectIsLoadingCursor,
  selectPlacingTemplate,
  selectTransientFunnel,
} from '../../../../reducers/canvasPageStateReducer';

import { useCallback, useContext, useMemo } from 'react';
import { selectAllVisibilityMaps } from '../../../../reducers/canvasLayersReducer';
import { VisibilityLayers } from '../../../../types/enums/VisibilityLayers';
import { useCalculatePills } from '../../../canvas-pills/useCalculatePills';
import { useConfigurationCanvasContext } from '../../configuration-canvas/ConfigurationCanvasContext';
import { visibilityMapToLayersSelectorVisibilityMap } from '../../configuration-canvas/canvas-container/layers-selector/utils';
import { useConfigurationCanvasState } from '../../configuration-canvas/useConfigurationCanvasState';
import { useConnectSteps } from '../../funnel-map-flow/dialogs/connect-steps-dialog/useConnectStep';
import { ConnectStep } from '../../funnel-map-flow/dialogs/connect-steps-dialog/ConnectSteps';
import {
  removeMetadataElementFunnelConnection,
  removeSectionAndContents,
  removeStepFunnelConnection,
  removeThirdPartyFunnelStepFunnelConnection,
} from '../../../../reducers/united-canvas/unitedCanvasReducer';
import {
  CanvasPillTypes,
  GroupLabelEntityType,
  GroupPillEntityType,
  NodeEntityTypes,
} from '../../../sweep-canvas/canvas-types';
import { useFitAroundElements } from './useFitAroundElements';
import { useGetReservedSpaceForPanels } from '../../configuration-canvas/panels-reserve-space/ReserveSpaceForPanelsCtx';
import { useSfFunnelPlugins } from '../../funnel-map-flow/templates/funnel-map-plugins/useSfFunnelPlugins';
import { PlacingPluginLayerConsumer } from '../../funnel-map-flow/templates/placing-plugin/PlacingPluginLayer';
import useObjectTypesWithFetch from '../../../../hooks/useObjectTypesWithFetch';
import {
  FunnelMapCanvasProps,
  OnRemoveFunnelLink,
  OnThirdPartyLabelClick,
  OnThirdPartyStepClick,
} from '../../../funnel-map-canvas/types';
import { FunnelType } from '../../../../types/enums/FunnelType';
import {
  setEditingThirdPartyFunnelId,
  setEditThirdPartyStep,
} from '../../../third-party-funnels/thirdPartyFunnelsReducer';
import { UnitedCanvasThirdPartyFunnelComponents } from '../../../third-party-funnels/UnitedCanvasThirdPartyFunnel';
import useStagesWithSparkleIcon from './useStagesWithSparkleIcon';
import { CanvasZeroStateRouter } from './canvas-zero-state-router/CanvasZeroStateRouter';
import { selectCrmOrgs } from '../../environments/environmentsReducer';
import { useMetadataMoreCtx } from '../../funnel-map-flow/dialogs/metadata-elements/more-menu/MetatadataMoreCtx';
import { ConnectedMetadataMorePanel } from '../../funnel-map-flow/dialogs/metadata-elements/more-menu/ConnectedMetadataMorePanel';
import { useDependenciesPanel } from '../../funnel-map-flow/dialogs/dependencies/useDependenciesPanel';
import { ErrorBoundary } from '@sentry/react';
import { selectFunnelStatusMap } from '../../../../reducers/funnelStatusReducer';
import { SfFunnelMenuContext } from '../../funnel-map-flow/dialogs/sf-funnel-menu/SfFunnelMenuProvider';
import { CanvasStepConnectedSfFunnelMenu } from '../../funnel-map-flow/dialogs/sf-funnel-menu/CanvasStepConnectedSfFunnelMenu';
import { BlockingOverlay } from './BlockingOverlay';
import { useFeatureToggle } from '../../../../feature-flags/useFeatureToggle';
import { useExpandedMode } from '../../configuration-canvas/panels-reserve-space/useExpandedMode';
import { useProcessMining } from './useProcessMining';
import useAgentsHome from '../../../aiAgentCenter/useAgentsHome';
import { CanvasToolbar } from './canvas-toolbar/CanvasToolbar';
import { DemoQuickActionsPanel } from '../../../../dreamforce-demo/components/DemoQuickActionsPanel';
import { OnSectionDeleteClick } from '../../../sweep-canvas/internal-context/SweepCanvasPropsCtx';
import useConfirm from '../../../common/dialogs/ConfirmLeaveWithoutSave/useConfirm';
import { SectionDeleteConfirmationDialog } from './SectionDeleteConfirmationDialog';
import { CanvasToolbarProvider } from './canvas-toolbar/CanvasToolbarCtx';

interface UnitedConnectedFunnelMapFlowCanvasProps {
  onSweepElementsChange: FunnelMapCanvasProps['onSweepElementsChange'];
  canvasNodesMouseMove: FunnelMapCanvasProps['canvasNodesMouseMove'];
}

export const UnitedConnectedFunnelMapFlowCanvas = ({
  canvasNodesMouseMove,
  onSweepElementsChange,
}: UnitedConnectedFunnelMapFlowCanvasProps) => {
  const dispatch = useDispatch();
  const placingTemplate = useSelector(selectPlacingTemplate);
  const transientFunnel = useSelector(selectTransientFunnel);
  const isLoadingCursor = useSelector(selectIsLoadingCursor);
  const { orgPillsMap, canvasFunnelMap: funnelMap } = useConfigurationCanvasState();
  const { canvasCrmOrgId: crmOrgId } = useConfigurationCanvasContext();
  useCalculatePills({ funnelMap });
  const { rightReservedSpace } = useGetReservedSpaceForPanels();
  const { objectTypesByName } = useObjectTypesWithFetch({ crmOrgId });
  const funnelStatusMap = useSelector(selectFunnelStatusMap);
  const { sfFunnelMoreButton } = useContext(SfFunnelMenuContext);
  const { canvasToolbar, dreamforceDemo } = useFeatureToggle();
  const { isCreateAgentActive } = useAgentsHome();
  const { isLoading: isProcessMiningLoading } = useProcessMining(funnelMap.id || '');
  const isCanvasDoingSomething = isCreateAgentActive || isProcessMiningLoading;
  const { isExpandedMode } = useExpandedMode();

  const {
    configurationCanvasProps: {
      visibilityElements,
      onFunnelStepClick,
      onFunnelLabelClick,
      onFunnelLabelPillClick,
      onStageGateClick,
      selectedStageId,
      selectedGateId,
      selectedFunnelId,
    },
  } = useConfigurationCanvasContext();

  const allVisibilityMaps = useSelector(selectAllVisibilityMaps);
  const visibilityMap = useMemo(() => {
    return visibilityMapToLayersSelectorVisibilityMap(
      visibilityElements.type === 'map'
        ? {
            // Remove old layers from the canvas if present
            [VisibilityLayers.NURTURING_STEPS]: true,
            [VisibilityLayers.GATES]: false,
            ...visibilityElements.visibilityMap,
          }
        : allVisibilityMaps?.[visibilityElements.layersId] || {},
    );
  }, [allVisibilityMaps, visibilityElements]);

  const { placePlugin, onPluginClick, isPlacingPlugin } = useSfFunnelPlugins();

  const { connectStepsProps, handleConnectStepsButtonClick, holdNodeHighlighted } =
    useConnectSteps(funnelMap);

  const onRemoveFunnelLinkClick: OnRemoveFunnelLink = useCallback(
    ({
      linkId,
      originFunnelType,
      sourceFunnelId,
      targetFunnelId,
      sourceStageId,
      targetStageId,
    }) => {
      if (originFunnelType === FunnelType.SALESFORCE) {
        dispatch(
          removeStepFunnelConnection({
            sourceFunnelId,
            sourceStageId,
            targetFunnelId,
            targetStageId,
          }),
        );
      }
      if (originFunnelType === FunnelType.THIRD_PARTY) {
        dispatch(
          removeThirdPartyFunnelStepFunnelConnection({
            sourceFunnelId,
            linkId,
            sourceStageId,
          }),
        );
      }
      if (originFunnelType === FunnelType.METADATA_ELEMENT) {
        dispatch(
          removeMetadataElementFunnelConnection({
            originFullMetadataId: sourceFunnelId,
            targetFunnelId,
            targetStageId,
          }),
        );
      }
    },
    [dispatch],
  );

  const { fitOnElements } = useFitAroundElements({
    funnelsData: funnelMap.funnelsData,
    recordTypesData: funnelMap.recordTypesData,
  });

  const _onFunnelLabelClick = useCallback(
    (props: {
      funnelId: string;
      entity: GroupPillEntityType | GroupLabelEntityType;
      event: React.MouseEvent | React.TouchEvent;
    }) => {
      if (
        props.entity.type === NodeEntityTypes.GroupPill &&
        props.entity.canvasPillType === CanvasPillTypes.groupAggregated
      ) {
        fitOnElements([props.funnelId], true);
        return;
      }
      onFunnelLabelClick?.(props);
    },
    [fitOnElements, onFunnelLabelClick],
  );

  const onThirdPartyLabelClick: OnThirdPartyLabelClick = useCallback(
    ({ funnelId }: { funnelId: string }) => {
      dispatch(setEditingThirdPartyFunnelId(funnelId));
    },
    [dispatch],
  );

  const onThirdPartyStepClick: OnThirdPartyStepClick = useCallback(
    ({ stepId, funnelId }: { stepId: string; funnelId: string }) => {
      dispatch(setEditThirdPartyStep({ funnelId, stepId }));
    },
    [dispatch],
  );
  const stagesWithSparkleIcon = useStagesWithSparkleIcon(funnelMap);

  const crmOrgs = useSelector(selectCrmOrgs);

  const { metadataMorePanelState, handleMetadataMoreClick, handleMetadataMoreClose } =
    useMetadataMoreCtx();
  const { openDependenciesPanel } = useDependenciesPanel();

  const handleMetadataClick = useCallback(
    ({
      event,
      fullMetadataId,
      entity,
    }: {
      event: React.MouseEvent | React.TouchEvent;
      fullMetadataId: string;
      entity: 'more-button' | 'node';
    }) => {
      if (entity === 'more-button') {
        handleMetadataMoreClick({ event, fullMetadataId });
      }
      if (entity === 'node') {
        const metadataElement = funnelMap.metadataElements[fullMetadataId];
        if (metadataElement) {
          openDependenciesPanel({
            crmOrgId: metadataElement.crmOrgId,
            metadataElementId: metadataElement.metadataId,
            color: metadataElement.color,
          });
        }
      }
    },
    [funnelMap.metadataElements, handleMetadataMoreClick, openDependenciesPanel],
  );
  const { openConfirm, onConfirm, onCancel } = useConfirm();

  const handleSectionDeleteClick: OnSectionDeleteClick = useCallback(
    async (sectionId: string) => {
      const isConfirmed = await openConfirm(
        <SectionDeleteConfirmationDialog closeDialog={onCancel} onConfirm={onConfirm} />,
      );
      if (isConfirmed) {
        dispatch(removeSectionAndContents({ sectionId }));
      }
    },
    [onCancel, onConfirm, openConfirm, dispatch],
  );

  return (
    <CanvasToolbarProvider>
      <ErrorBoundary>
        <PlacingPluginLayerConsumer />
        {connectStepsProps.anchorEl && (
          <ConnectStep
            anchorEl={connectStepsProps.anchorEl}
            handleClose={connectStepsProps.handleClose}
            stepGroups={connectStepsProps.stepGroups}
            onItemClick={connectStepsProps.onItemClick}
          />
        )}
      </ErrorBoundary>
      <CanvasStepConnectedSfFunnelMenu />
      <UnitedCanvasThirdPartyFunnelComponents funnelMap={funnelMap} />
      <CanvasZeroStateRouter />
      {metadataMorePanelState && (
        <ConnectedMetadataMorePanel
          funnelMap={funnelMap}
          metadataMorePanelState={metadataMorePanelState}
          handleMetadataMoreClose={handleMetadataMoreClose}
          key={metadataMorePanelState.fullMetadataId}
        />
      )}
      <BlockingOverlay isVisible={isLoadingCursor} />
      {canvasToolbar && !isExpandedMode && <CanvasToolbar isDisabled={isCanvasDoingSomething} />}
      {dreamforceDemo && <DemoQuickActionsPanel />}

      <FunnelMapCanvas
        funnelMap={funnelMap}
        visibilityMap={visibilityMap}
        onStageGateClick={onStageGateClick}
        onFunnelStepClick={onFunnelStepClick}
        onFunnelLabelClick={onFunnelLabelClick ? _onFunnelLabelClick : undefined}
        onFunnelLabelPillClick={onFunnelLabelPillClick ? onFunnelLabelPillClick : undefined}
        orgPillsMap={orgPillsMap}
        isLoadingCursor={isLoadingCursor}
        canvasNodesMouseMove={canvasNodesMouseMove}
        autoFitViewOnFirstNodes
        holdNodeHighlighted={holdNodeHighlighted}
        onRemoveFunnelLinkClick={onRemoveFunnelLinkClick}
        onConnectFunnelOrStepsClick={handleConnectStepsButtonClick}
        onSweepElementsChange={onSweepElementsChange}
        selectedStageId={selectedStageId}
        selectedGateId={selectedGateId}
        selectedFunnelId={selectedFunnelId}
        placingTemplate={placingTemplate}
        transientFunnel={transientFunnel}
        onPluginClick={onPluginClick}
        onFunnelOverlayClick={isPlacingPlugin ? placePlugin : undefined}
        controlsRightMargin={rightReservedSpace}
        objectTypesByName={objectTypesByName}
        onThirdPartyLabelClick={onThirdPartyLabelClick}
        onThirdPartyStepClick={onThirdPartyStepClick}
        stagesWithSparkleIcon={stagesWithSparkleIcon}
        crmOrgs={crmOrgs}
        onMetadataClick={handleMetadataClick}
        zoomToFitOnFirstNodes
        funnelStatusMap={funnelStatusMap}
        sfFunnelMoreButton={sfFunnelMoreButton}
        disableMultiSelection={canvasToolbar}
        onSectionDeleteClick={handleSectionDeleteClick}
      />
    </CanvasToolbarProvider>
  );
};
